import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';
import { useDebounce } from '@/lib/useDebounce';

interface CustomerHit { id: string; fullName: string; phone: string | null; email: string | null }
interface LeadHit { id: string; name: string; status: string; vehicleDesc: string }
interface QuoteJobHit { id: string; number: number; status: string; totalCents: number; customerName: string }
interface VehicleHit { id: string; year: number | null; make: string; model: string; vinLast4: string | null; customerId: string }

interface GlobalSearchResult {
  customers: CustomerHit[];
  leads: LeadHit[];
  quotes: QuoteJobHit[];
  jobs: QuoteJobHit[];
  vehicles: VehicleHit[];
}

export function GlobalSearch({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 250);

  const { data, isFetching } = useQuery({
    queryKey: [workspaceId, 'global-search', debouncedQuery],
    queryFn: () => api.get<GlobalSearchResult>(workspacePath(workspaceId!, `/search?q=${encodeURIComponent(debouncedQuery)}`)),
    enabled: !!workspaceId && debouncedQuery.length >= 2,
  });

  function go(path: string) {
    navigate(path);
    setOpen(false);
    setQuery('');
  }

  const hasQuery = debouncedQuery.length >= 2;
  const hasResults =
    !!data && (data.customers.length || data.leads.length || data.quotes.length || data.jobs.length || data.vehicles.length);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={t('topbar.searchPlaceholder')}
          className="hidden w-[230px] items-center gap-2 border border-border bg-surface-2 px-2.5 py-1.5 text-[13px] text-muted hover:bg-surface-2/70 lg:flex"
        >
          <Search size={13} strokeWidth={1.8} className="flex-none" />
          <span className="truncate">{t('topbar.searchPlaceholder')}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={6} className="z-50 w-[360px] border border-border bg-surface p-1.5 shadow-md">
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('topbar.searchPlaceholder')}
            className="w-full border border-border bg-surface-2 px-2.5 py-2 text-sm text-ink outline-none placeholder:text-muted"
          />
          <div className="mt-1.5 flex max-h-96 flex-col overflow-y-auto">
            {!hasQuery ? null : isFetching ? (
              <p className="px-2.5 py-4 text-center text-sm text-muted">{t('topbar.searching')}</p>
            ) : !hasResults ? (
              <p className="px-2.5 py-4 text-center text-sm text-muted">{t('topbar.noResults')}</p>
            ) : (
              <>
                <ResultGroup label={t('topbar.searchCustomers')}>
                  {data!.customers.map((c) => (
                    <ResultRow key={c.id} onClick={() => go(`/customers/${c.id}`)}>
                      {c.fullName}
                    </ResultRow>
                  ))}
                </ResultGroup>
                <ResultGroup label={t('topbar.searchLeads')}>
                  {data!.leads.map((l) => (
                    <ResultRow key={l.id} onClick={() => go(`/leads?leadId=${l.id}`)}>
                      {l.name}
                      {l.vehicleDesc ? <span className="ml-1 text-muted">· {l.vehicleDesc}</span> : null}
                    </ResultRow>
                  ))}
                </ResultGroup>
                <ResultGroup label={t('topbar.searchQuotes')}>
                  {data!.quotes.map((q) => (
                    <ResultRow key={q.id} onClick={() => go(`/quotes/${q.id}`)}>
                      #{q.number} · {q.customerName}
                    </ResultRow>
                  ))}
                </ResultGroup>
                <ResultGroup label={t('topbar.searchJobs')}>
                  {data!.jobs.map((j) => (
                    <ResultRow key={j.id} onClick={() => go(`/jobs/${j.id}`)}>
                      #{j.number} · {j.customerName}
                    </ResultRow>
                  ))}
                </ResultGroup>
                <ResultGroup label={t('topbar.searchVehicles')}>
                  {data!.vehicles.map((v) => (
                    <ResultRow key={v.id} onClick={() => go(`/customers/${v.customerId}`)}>
                      {[v.year, v.make, v.model].filter(Boolean).join(' ')}
                      {v.vinLast4 ? <span className="ml-1 text-muted">· {v.vinLast4}</span> : null}
                    </ResultRow>
                  ))}
                </ResultGroup>
              </>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function ResultGroup({ label, children }: { label: string; children: ReactNode }) {
  const items = Array.isArray(children) ? children.filter(Boolean) : children;
  if (Array.isArray(items) && items.length === 0) return null;
  return (
    <div className="flex flex-col">
      <span className="px-2.5 pt-2 text-[11px] font-bold uppercase tracking-wide text-muted">{label}</span>
      {items}
    </div>
  );
}

function ResultRow({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center px-2.5 py-2 text-left text-sm hover:bg-surface-2">
      {children}
    </button>
  );
}
